import React, { useCallback, useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import { analyticsHeadersData } from "../data/tableData";
import { MobileResponsive } from "./MobileResponsive";
import useDataFetch from "../hooks/useDataFetch";
import { updateDashboardData } from "../features/dashboardSlice";
import DashboardTable from "../components/DashboardTable";
import { Link } from "react-router-dom";
import { Map } from "@vis.gl/react-google-maps";
import { MapMarker } from "../components/MapMarker";
import { InfoWindowPopup } from "../components/InfoWindow";
import { getEntityData, getH3HexagonRadius, getHexCenter } from "../utils/mapUtils";
import { DeckGLOverlay } from "../components/MapOverlay";
import { ScatterplotLayer } from "deck.gl";
import DynamicMetaTags from "../components/DynamicMetaTags";
import NotificationBell from "../components/NotificationBell";
import { fetchNotificationsApi } from "../api/services/notificationService";

const PAGE_SIZE = 15;

const HomePage = () => {
  const { data, isLoading, mutate, isValidating } = useDataFetch(
    "/admin/home",
    updateDashboardData
  );
  const { data: profileData, isLoading: profileLoading } =
    useDataFetch(`/admin/profile`);

  const [selectedStatus, setSelectedStatus] = useState("Pending");
  const [ordersPage, setOrdersPage] = useState(1);

  const buildOrdersApiUrl = () => {
    const params = new URLSearchParams();
    params.append("page", ordersPage);
    params.append("time", "all");
    params.append("statusCategory", selectedStatus);
    return `/admin/payments?${params.toString()}`;
  };

  const {
    data: ordersData,
    isLoading: ordersLoading,
    mutate: mutateOrders,
  } = useDataFetch(buildOrdersApiUrl());

  const counts = data?.counts || {};
  const orderStatusCounts = data?.orderStatusCounts || {};
  const orders = ordersData?.orders || [];
  const totalOrders = ordersData?.total || ordersData?.ordersCount || 0;

  const [selectedMarker, setSelectedMarker] = useState({
    selectedMarker: null,
    entityData: [],
  });
  const [infoWindowShown, setInfoWindowShown] = useState(false);
  const [entity, setEntity] = useState("");

  useEffect(() => {
    const intervalId = setInterval(() => {
      mutate();
    }, 90000);

    return () => {
      clearInterval(intervalId);
    };
  }, [mutate]);

  const handleSync = () => {
    mutate();
    mutateOrders();
  };

  const handleMarkerClick = (id, marker, entityType) => {
    setEntity(entityType);
    setSelectedMarker(() => ({
      selectedMarker: marker,
      entityData: getEntityData(id, entityType, data),
    }));
    setInfoWindowShown(true);
  };

  const handleCloseInfoWindow = () => {
    setInfoWindowShown(false);
    setSelectedMarker({
      selectedMarker: null,
      entityData: null,
    });
  };

  const onMapClick = useCallback(() => {
    setSelectedMarker({
      selectedMarker: null,
      entityData: [],
    });
    setInfoWindowShown(false);
  }, []);

  useEffect(() => {
    if (
      selectedMarker.entityData?.length > 0 &&
      selectedMarker.selectedMarker
    ) {
      setInfoWindowShown(true);
    }
  }, [selectedMarker]);

  const handleStatusClick = (status) => {
    setSelectedStatus(status);
    setOrdersPage(1);
  };

  const getStateClass = (state) =>
    selectedStatus === state
      ? "border-none bg-quinary text-white"
      : "border-2 border-quinary text-secondary";

  const getGreeting = () => {
    const currentHour = new Date().getHours();
    if (currentHour < 12) {
      return "Good Morning";
    } else if (currentHour < 18) {
      return "Good Afternoon";
    } else {
      return "Good Evening";
    }
  };

  return (
    <>
      <DynamicMetaTags
        pageTitle="Beep - Your Real-Time Logistics Dashboard"
        pageDescription="Get real-time insights with Beep's dashboard! Monitor active drivers, food outlets, hubs, and detailed analytics on orders. Track Pending, Delivering, and Done orders seamlessly across outlets and hubs."
        pageKeywords="Beep, logistics dashboard, real-time analytics, active drivers, active food outlets, active hubs, order tracking, pending orders, delivering orders, completed orders, logistics insights"
      />
      <MobileResponsive />
      <div className="relative lg:flex hidden items-start">
        <Sidebar />

        <div className="ml-[14.5vw] w-full h-screen overflow-y-auto px-10 py-8 text-secondary flex items-start">
          <div className="flex flex-col 2xl:w-full xl:w-full lg:w-full md:w-full sm:w-full">
            <div className="flex justify-between w-[95.5%]">
              <div className="flex items-center gap-3">
                <span className="font-semibold text-[24px] capitalize">
                  {getGreeting()},{" "}
                  {profileLoading ? `Admin` : profileData?.name?.split(" ")[0]}!
                </span>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleSync}
                  disabled={isValidating || ordersLoading}
                  className="rounded-full bg-[#F7631D] px-5 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-[#d55419] disabled:cursor-not-allowed disabled:bg-gray-300"
                >
                  {isValidating || ordersLoading ? "Syncing..." : "Sync immediately"}
                </button>
                <NotificationBell fetchNotificationsApi={fetchNotificationsApi} />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-x-3 gap-y-6 w-[95.5%] py-5">
              <Link
                to="/drivers?st=active"
                className="border-[2.33px] border-primary h-28 rounded-lg text-secondary text-center flex flex-col justify-center items-center gap-1.5"
              >
                <h5 className="text-sm font-semibold tracking-wide">
                  Active Driver
                </h5>
                <p>
                  <span className="text-2xl font-medium pr-2">
                    {counts?.activeDriversCount || 0}
                  </span>
                  <span className="text-sm font-normal text-quinary">
                    ({counts?.driversOnBreakCount || 0} on break)
                  </span>
                </p>
                <p className="text-sm font-medium text-ternary tracking-wide">
                  Total: {counts?.totalDriversCount || 0}
                </p>
              </Link>
              <Link
                to="/outlets?st=active"
                className="border-[2.33px] border-primary h-28 rounded-lg text-secondary text-center flex flex-col justify-center items-center gap-1.5"
              >
                <h5 className="text-sm font-semibold tracking-wide">
                  Active Food Outlet
                </h5>
                <p>
                  <span className="text-2xl font-medium pr-2">
                    {counts?.activeFoodOutletsCount || 0}
                  </span>
                </p>
                <p className="text-sm font-medium text-ternary tracking-wide">
                  Total: {counts?.totalFoodOutletsCount || 0}
                </p>
              </Link>
              <Link
                to="/active-hubs"
                className="border-[2.33px] border-primary h-28 rounded-lg text-secondary text-center flex flex-col justify-center items-center gap-1.5"
              >
                <h5 className="text-sm font-semibold tracking-wide">
                  Active hubs
                </h5>
                <p>
                  <span className="text-2xl font-medium pr-2">
                    {counts?.activeHotspotsCount || 0}
                  </span>
                </p>
              </Link>
            </div>

            <div className="py-8 w-[95.5%]">
              <div className="flex items-center justify-between">
                <h6 className="text-[17px] font-semibold">Analytics</h6>
                <Link
                  to="/orders"
                  className="rounded-full border border-[#F7631D] px-5 py-1.5 text-sm font-semibold text-[#F7631D] transition-colors hover:bg-[#F7631D] hover:text-white"
                >
                  See all orders
                </Link>
              </div>

              <div className="grid grid-cols-3 gap-x-3 gap-y-6 py-5">
                {["Pending", "Delivering", "Done"].map((status) => (
                  <div
                    key={status}
                    onClick={() => handleStatusClick(status)}
                    className={`h-20 rounded-lg text-center flex flex-col justify-center items-center gap-1.5 cursor-pointer ${getStateClass(status)}`}
                  >
                    <h5 className="text-base font-semibold tracking-wide">
                      {status}
                    </h5>
                    <p className="text-xl font-medium pr-2">
                      {orderStatusCounts?.[status] || 0}
                    </p>
                  </div>
                ))}
              </div>

              <div className="flex flex-col gap-5 w-full">
                {ordersLoading ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="h-10 w-10 rounded-full border-4 border-t-quaternary border-b-quaternary border-r-transparent border-l-transparent animate-spin"></div>
                  </div>
                ) : (
                  <DashboardTable
                    headers={analyticsHeadersData}
                    data={orders}
                    currentPage={ordersPage}
                    totalCount={totalOrders}
                    pageSize={PAGE_SIZE}
                    onPageChange={setOrdersPage}
                    useServerPagination
                  />
                )}
              </div>
            </div>
          </div>

          <div className="w-[48%] h-screen">
            <div className="2xl:w-[100%] xl:w-full lg:w-full md:w-full sm:w-full h-[92%] shadow-default rounded-xl flex justify-center items-center overflow-hidden">
              {isLoading ? (
                <div className="w-full h-full flex flex-col items-center justify-center gap-4">
                  <div className="w-full h-full bg-gray-200 rounded-lg animate-pulse"></div>
                </div>
              ) : (
                <Map
                  mapId={"terrain"}
                  defaultZoom={6}
                  defaultCenter={{
                    lat: 54.5,
                    lng: -3.0,
                  }}
                  onClick={onMapClick}
                  gestureHandling={"greedy"}
                  disableDefaultUI
                  styles={"terrain"}
                  fullscreenControl={true}
                  mapTypeId={"terrain"}
                  isFractionalZoomEnabled={true}
                  mapTypeControlOptions
                  zoomControl
                  scaleControl
                  streetViewControl
                  mapTypeControl
                  cameraControl
                  rotateControl
                >
                  <MapMarker
                    data={data?.orderMarkers}
                    entityType="orders"
                    onMouseEnter={handleMarkerClick}
                  />

                  <MapMarker
                    data={data?.outletMarkers}
                    entityType="outlets"
                    onMouseEnter={handleMarkerClick}
                  />

                  {data?.hotspotMarkers?.map((hotspot) => {
                    const [lat, lng] = getHexCenter(hotspot?.hexString);
                    const radius = getH3HexagonRadius(hotspot?.hexString);

                    const layers = [
                      new ScatterplotLayer({
                        id: `deckgl-circle-${hotspot?._id || hotspot?.hexString}`,
                        data: [{ position: [lat, lng] }],
                        getPosition: (d) => d.position,
                        getFillColor: [179, 20, 18, 150],
                        getRadius: radius * 1000,
                      }),
                    ];

                    return (
                      <React.Fragment key={hotspot?._id || hotspot?.hexString}>
                        <MapMarker
                          data={[
                            {
                              lat: lng,
                              lng: lat,
                              hotspot,
                            },
                          ]}
                          entityType="hubspots"
                          onMouseEnter={handleMarkerClick}
                        />

                        <DeckGLOverlay layers={layers} />
                      </React.Fragment>
                    );
                  })}

                  <InfoWindowPopup
                    infoWindowShown={infoWindowShown}
                    selectedMarker={selectedMarker}
                    type={entity}
                    handleCloseInfoWindow={handleCloseInfoWindow}
                  />
                </Map>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HomePage;
